// E2E encryption helpers using Web Crypto API.
// Passphrase -> PBKDF2 -> AES-GCM 256-bit key. Server only ever sees ciphertext + salt.

const enc = new TextEncoder();
const dec = new TextDecoder();

function b64encode(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
function b64decode(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function deriveKey(passphrase, saltBytes) {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(passphrase),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: saltBytes, iterations: 250000, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function generateSalt() {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return b64encode(salt);
}

export async function createProbe(passphrase, saltB64) {
  const salt = b64decode(saltB64);
  const key = await deriveKey(passphrase, salt);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode("labtracker-probe-v1")
  );
  return { salt: saltB64, iv: b64encode(iv), ciphertext: b64encode(ct) };
}

export async function verifyProbe(passphrase, probe) {
  try {
    const salt = b64decode(probe.salt);
    const key = await deriveKey(passphrase, salt);
    const iv = b64decode(probe.iv);
    const ct = b64decode(probe.ciphertext);
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
    return dec.decode(pt) === "labtracker-probe-v1";
  } catch {
    return false;
  }
}

export async function deriveLabKey(passphrase, saltB64) {
  return deriveKey(passphrase, b64decode(saltB64));
}

export async function encryptText(key, plaintext) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(plaintext)
  );
  return { iv: b64encode(iv), ciphertext: b64encode(ct) };
}

export async function decryptText(key, ivB64, ctB64) {
  try {
    const iv = b64decode(ivB64);
    const ct = b64decode(ctB64);
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
    return dec.decode(pt);
  } catch {
    return null;
  }
}

// Session-scoped key cache (never persisted)
const keyCache = new Map();
export function cacheLabKey(labId, key) { keyCache.set(labId, key); }
export function getCachedLabKey(labId) { return keyCache.get(labId); }
export function clearLabKey(labId) { keyCache.delete(labId); }
